import { TrashIcon } from "@heroicons/react/24/outline";
import {
  CONDITION_1_KEYS,
  CONDITION_2_KEYS,
  CONDITION_OPERATORS,
  EVENT_KEYS,
  INPUT_CLASS_NAME,
} from "../constants";
import { Field, SelectField, TextAreaField } from "./FormFields";

export default function EventChunkEditor({ chunk, index, canRemove, onChange, onRemove }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Event {index + 1}</h3>
        {canRemove ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-red-600 hover:bg-red-50"
            onClick={() => onRemove(chunk.id)}
          >
            <TrashIcon className="h-4 w-4" />
            Remove
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label htmlFor={`event_key_${chunk.id}`} className="block text-sm font-semibold text-gray-900">
            Event key
          </label>
          <select
            id={`event_key_${chunk.id}`}
            name={`event_key[${index}]`}
            value={chunk.event_key}
            onChange={(event) => onChange(chunk.id, "event_key", event.target.value)}
            className={INPUT_CLASS_NAME}
          >
            {EVENT_KEYS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <Field
          id={`event_name_en_${chunk.id}`}
          name={`event_name_en[${index}]`}
          label="Event name (EN)"
          value={chunk.event_name_en}
          onChange={(event) => onChange(chunk.id, "event_name_en", event.target.value)}
          placeholder="Enter English event name"
        />
        <Field
          id={`event_name_hi_${chunk.id}`}
          name={`event_name_hi[${index}]`}
          label="Event name (HI)"
          value={chunk.event_name_hi}
          onChange={(event) => onChange(chunk.id, "event_name_hi", event.target.value)}
          placeholder="Enter Hindi event name"
        />
        <TextAreaField
          id={`event_description_en_${chunk.id}`}
          name={`event_description_en[${index}]`}
          label="Description (EN)"
          rows={3}
          value={chunk.description_en}
          onChange={(event) => onChange(chunk.id, "description_en", event.target.value)}
          placeholder="Enter English description"
        />
        <TextAreaField
          id={`event_description_hi_${chunk.id}`}
          name={`event_description_hi[${index}]`}
          label="Description (HI)"
          rows={3}
          value={chunk.description_hi}
          onChange={(event) => onChange(chunk.id, "description_hi", event.target.value)}
          placeholder="Enter Hindi description"
        />
        <Field
          id={`coins_${chunk.id}`}
          name={`coins[${index}]`}
          label="Coins"
          type="number"
          value={chunk.coins}
          onChange={(event) => onChange(chunk.id, "coins", event.target.value)}
          placeholder="0"
        />
        <Field
          id={`min_action_${chunk.id}`}
          name={`min_action[${index}]`}
          label="Min action"
          type="number"
          value={chunk.min_action}
          onChange={(event) => onChange(chunk.id, "min_action", event.target.value)}
          placeholder="0"
        />
        <SelectField
          id={`condition_1_key_${chunk.id}`}
          name={`condition_1_key[${index}]`}
          label="Condition 1 key"
          value={chunk.condition_1_key}
          onChange={(event) => onChange(chunk.id, "condition_1_key", event.target.value)}
          options={CONDITION_1_KEYS}
        />
        <SelectField
          id={`condition_1_${chunk.id}`}
          name={`condition_1[${index}]`}
          label="Condition 1"
          value={chunk.condition_1}
          onChange={(event) => onChange(chunk.id, "condition_1", event.target.value)}
          options={CONDITION_OPERATORS}
        />
        <Field
          id={`condition_1_value_${chunk.id}`}
          name={`condition_1_value[${index}]`}
          label="Condition 1 value"
          type="number"
          value={chunk.condition_1_value}
          onChange={(event) => onChange(chunk.id, "condition_1_value", event.target.value)}
          placeholder="0"
        />
        <SelectField
          id={`condition_2_key_${chunk.id}`}
          name={`condition_2_key[${index}]`}
          label="Condition 2 key"
          value={chunk.condition_2_key}
          onChange={(event) => onChange(chunk.id, "condition_2_key", event.target.value)}
          options={CONDITION_2_KEYS}
        />
        <SelectField
          id={`condition_2_${chunk.id}`}
          name={`condition_2[${index}]`}
          label="Condition 2"
          value={chunk.condition_2}
          onChange={(event) => onChange(chunk.id, "condition_2", event.target.value)}
          options={CONDITION_OPERATORS}
        />
        <Field
          id={`condition_2_value_${chunk.id}`}
          name={`condition_2_value[${index}]`}
          label="Condition 2 value"
          type="number"
          value={chunk.condition_2_value}
          onChange={(event) => onChange(chunk.id, "condition_2_value", event.target.value)}
          placeholder="0"
        />
        <div>
          <p className="block text-sm font-semibold text-gray-900">Is streak</p>
          <div className="mt-2 flex flex-wrap gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-gray-900">
              <input
                type="radio"
                name={`is_streak[${index}]`}
                value="yes"
                checked={chunk.is_streak === true}
                onChange={() => onChange(chunk.id, "is_streak", true)}
                className="h-4 w-4 border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
              Yes
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-900">
              <input
                type="radio"
                name={`is_streak[${index}]`}
                value="no"
                checked={chunk.is_streak === false}
                onChange={() => onChange(chunk.id, "is_streak", false)}
                className="h-4 w-4 border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
              No
            </label>
          </div>
        </div>
        {chunk.is_streak ? (
          <>
            <Field
              id={`streak_duration_${chunk.id}`}
              name={`streak_duration[${index}]`}
              label="Streak duration"
              type="number"
              value={chunk.streak_duration}
              onChange={(event) => onChange(chunk.id, "streak_duration", event.target.value)}
              placeholder="0"
            />
            <Field
              id={`streak_coins_${chunk.id}`}
              name={`streak_coins[${index}]`}
              label="Streak coins"
              type="number"
              value={chunk.streak_coins}
              onChange={(event) => onChange(chunk.id, "streak_coins", event.target.value)}
              placeholder="0"
            />
          </>
        ) : (
          <>
            <input type="hidden" name={`streak_duration[${index}]`} value="0" />
            <input type="hidden" name={`streak_coins[${index}]`} value="0" />
          </>
        )}
      </div>
    </div>
  );
}
